import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Button } from 'components/Button'
import { useAnalytics } from 'hooks'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/Dialog'
import { useEditorFocusStore } from '../store/conversation-store'
import { DraftCreativeProvider, useDraftCreativeContext } from '../store/draft-creative-context'
import { CREATOR_MODE_MAP, Creative, CreativeType } from '../types'
import { getFieldLabelById, isCarouselSlideFocus } from '../utils/carousel-slide-focus'
import { extractApiError } from '../utils/extract-api-error'
import { isUserMediaRefusal } from '../store/user-media-edit'
import { TYPES_WITH_EDITOR, getEditor } from './editorRegistry'
import { ScopedFieldProvider } from './ScopedFieldContext'
import { useComposerReadOnly } from './composer-read-only'

function platformFor(type: CreativeType): string | undefined {
  return CREATOR_MODE_MAP[type]
}

/**
 * Lets descendants of the editor body (e.g. VideoPickerBody's "Ask AI to
 * regenerate" button) discard the draft and close the modal — needed when
 * the user is being handed off to chat for the actual action.
 */
const EditorModalCloseContext = createContext<(() => void) | null>(null)
export function useEditorModalClose(): (() => void) | null {
  return useContext(EditorModalCloseContext)
}

interface EditorModalProps {
  creative: Creative
}

/**
 * Hosts the structured editor in a centered T3 dark modal, opened by per-section
 * Edit overlays on the preview. Edits are pending inside `DraftCreativeProvider`
 * and only commit to the real creative store on Save. Cancel / X / Esc / overlay
 * click all discard.
 *
 * Open trigger: a `useEditorFocusStore` request for this creative.id. The modal
 * remembers `open` locally — the inner editor's `useEditorHighlight` hook
 * consumes the focus on mount (to scroll-and-highlight), so we mustn't tie
 * `open` to the focus signal directly or the modal would close immediately.
 */
export function EditorModal({ creative }: EditorModalProps) {
  const { track } = useAnalytics()
  const readOnly = useComposerReadOnly()
  const focusRequest = useEditorFocusStore(s => s.focusRequests[creative.id])
  const consumeFocus = useEditorFocusStore(s => s.consumeFocus)

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  // Remember the section that opened the modal — useEditorHighlight will
  // consume + clear the focus on mount, so we capture it here for analytics.
  const openedFieldRef = useRef<string | null>(null)

  useEffect(() => {
    if (!focusRequest) return
    if (!TYPES_WITH_EDITOR.has(creative.type)) return
    if (open) return

    openedFieldRef.current = focusRequest
    setOpen(true)
    track(AnalyticsEvents.Composer.COMPOSER_EDIT_MODAL_OPENED, {
      creative_id: creative.id,
      creative_type: creative.type,
      platform: platformFor(creative.type),
      field_id: focusRequest,
    })
  }, [focusRequest, creative.id, creative.type, open, track])

  const handleClose = useCallback(() => {
    // Defensive: if a stale focus request is still in the store (e.g. user
    // re-clicked the same field while the modal was open), consume it so the
    // modal can reopen on the next click.
    if (useEditorFocusStore.getState().focusRequests[creative.id]) {
      consumeFocus(creative.id)
    }
    setOpen(false)
  }, [creative.id, consumeFocus])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      // Radix fires onOpenChange(false) for overlay click, X button, and Esc.
      // All three behave as Cancel — the Body component fires the analytics.
      // A save in flight can't be cancelled, so they wait for it.
      if (!next && !saving) handleClose()
    },
    [handleClose, saving]
  )

  if (!TYPES_WITH_EDITOR.has(creative.type)) return null
  // A live post is view-only — never mount the structured editor for it (the
  // section-hover Edit affordance is hidden too, this is the defensive backstop).
  if (readOnly) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent variant="dark" size="md" className="max-h-[85vh]">
        {open && (
          <DraftCreativeProvider creativeId={creative.id}>
            <EditorModalBody
              creative={creative}
              openedField={openedFieldRef.current}
              onClose={handleClose}
              saving={saving}
              onSavingChange={setSaving}
            />
          </DraftCreativeProvider>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface EditorModalBodyProps {
  creative: Creative
  openedField: string | null
  onClose: () => void
  saving: boolean
  onSavingChange: (saving: boolean) => void
}

function EditorModalBody({
  creative,
  openedField,
  onClose,
  saving,
  onSavingChange,
}: EditorModalBodyProps) {
  const { track } = useAnalytics()
  const ctx = useDraftCreativeContext()

  const draft = ctx?.draft ?? null
  const snapshot = ctx?.snapshot ?? null
  const isDirty = ctx?.isDirty ?? false
  const commit = ctx?.commit

  const titleText = useMemo(() => {
    if (openedField && isCarouselSlideFocus(openedField)) return 'Edit carousel'
    const label = getFieldLabelById(openedField)
    return label ? `Edit ${label}` : 'Edit'
  }, [openedField])

  // A carousel-slide focus (e.g. "carousel_slide_3") opens the editor scoped to
  // the "image" section so the FieldGroup that hosts MediaItemStrip survives
  // ScopedFieldProvider's filter. The raw slide key stays in the focus store —
  // MediaItemStrip consumes it on mount to highlight + scroll to the clicked
  // slide. The original key is preserved on openedFieldRef for analytics +
  // title (slide index has signal).
  const scopedFieldId = openedField && isCarouselSlideFocus(openedField) ? 'image' : openedField

  const fieldsChanged = useMemo(() => {
    if (!draft || !snapshot) return []
    const draftContent = draft.content as unknown as Record<string, unknown>
    const snapshotContent = snapshot.content as unknown as Record<string, unknown>
    const keys = new Set([...Object.keys(draftContent), ...Object.keys(snapshotContent)])
    const changed: string[] = []
    keys.forEach(k => {
      if (JSON.stringify(draftContent[k]) !== JSON.stringify(snapshotContent[k])) {
        changed.push(k)
      }
    })
    return changed
  }, [draft, snapshot])

  const [saveError, setSaveError] = useState<string | null>(null)

  const handleSave = useCallback(async () => {
    if (!commit) return
    onSavingChange(true)
    setSaveError(null)
    try {
      await commit()
    } catch (err) {
      if (isUserMediaRefusal(err)) console.log('Edit modal save refused:', err)
      else console.error('Edit modal save failed:', err)
      setSaveError(extractApiError(err, "We couldn't save your changes. Please try again."))
      onSavingChange(false)
      return
    }
    onSavingChange(false)
    track(AnalyticsEvents.Composer.COMPOSER_EDIT_MODAL_SAVED, {
      creative_id: creative.id,
      creative_type: creative.type,
      platform: platformFor(creative.type),
      field_id: openedField ?? undefined,
      fields_changed: fieldsChanged,
    })
    onClose()
  }, [
    commit,
    creative.id,
    creative.type,
    openedField,
    fieldsChanged,
    onClose,
    onSavingChange,
    track,
  ])

  const handleCancel = useCallback(() => {
    track(AnalyticsEvents.Composer.COMPOSER_EDIT_MODAL_CANCELLED, {
      creative_id: creative.id,
      creative_type: creative.type,
      platform: platformFor(creative.type),
      had_changes: isDirty,
    })
    onClose()
  }, [creative.id, creative.type, isDirty, onClose, track])

  if (!draft) return null

  return (
    <>
      <DialogHeader>
        <DialogTitle variant="dark">{titleText}</DialogTitle>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto">
        <EditorModalCloseContext.Provider value={onClose}>
          <ScopedFieldProvider fieldId={scopedFieldId}>{getEditor(draft)}</ScopedFieldProvider>
        </EditorModalCloseContext.Provider>
      </div>
      {saveError && <p className="text-xs text-[color:var(--alert-error-text)]">{saveError}</p>}
      <DialogFooter>
        <Button
          type="button"
          variant="t3-tertiary"
          size="t3-md"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="t3-primary"
          size="t3-md"
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          Save
        </Button>
      </DialogFooter>
    </>
  )
}
